'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Building2, ArrowRight, AlertCircle, CheckCircle, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthenticationService } from '@/lib/use-generated-lib';
import { useAuth } from '@/lib/auth';
import { useRouter } from 'next/navigation';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { constructOidcAuthorizationUrl, generateCodeVerifier, generateCodeChallenge, type OidcConfig } from '@/lib/auth-config';
import type { DomainLookupResult } from '@/lib/generated-api/models/DomainLookupResult';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses, getInputClasses, getAccentClasses } from '@/lib/theme-utils';
import { getThemeClass } from '@/lib/theme-helpers';
import { wipeAppAuthState } from '@/lib/wipe-app-auth-state';
import { clearSessionData } from '@/lib/session-management';
import { normalizeApiBaseUrl, getApiContextPath } from '@/lib/utils/url-normalization';
import { readStoredAccessToken } from '@/lib/auth-token-storage';
import { logger } from '@/lib/logger';

// Check if self-service enrollment is enabled
const enrollmentEnabled = false;

interface DomainConfig {
  hasSSO: boolean;
  domain?: string;
  customerId?: string;
  customerName?: string;
  providerType?: string;
  error?: string;
  authConfig?: Record<string, unknown>;
  auth_url?: string;
  ssoProvider?: string;
  // OIDC configuration from backend (provider-agnostic)
  oidcConfig?: OidcConfig;
  // Legacy: keycloakAlias for backward compatibility (now part of oidcConfig.idpAlias)
  keycloakAlias?: string;
}

type LoginMethod = 'local' | 'sso' | 'auto';

interface DomainAwareLoginProps {
  sessionMessage?: string | null;
}

export function DomainAwareLogin({ sessionMessage }: DomainAwareLoginProps) {
  const { theme } = useTheme();
  
  const [email, setEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [isDomainValid, setIsDomainValid] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [domainConfig, setDomainConfig] = useState<DomainConfig | null>(null);
  const [method, setMethod] = useState<LoginMethod>('local');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [signInTimedOut, setSignInTimedOut] = useState(false);
  const { authenticationService, isLoaded: authenticationServiceLoaded } = useAuthenticationService();
  const { login, user, session, currentTenant, isLoading: authLoading, authPhase } = useAuth();
  const router = useRouter();

  // Redirect when authenticated (single source: authPhase)
  useEffect(() => {
    if (authPhase !== 'authenticated' || !currentTenant?.tenantSlug) return;
    router.replace(`/t/${currentTenant.tenantSlug}/dashboard`);
  }, [authPhase, currentTenant?.tenantSlug, router]);

  // H01: When login is shown after expiry/invalid, wipe residual auth so a new email cannot inherit prior SSO/session
  useEffect(() => {
    if (authPhase !== 'expired' && authPhase !== 'error') return;
    let cancelled = false;
    const wipeResidual = async () => {
      try {
        let staleSessionId: string | undefined;
        try {
          const raw = localStorage.getItem('session');
          if (raw) {
            staleSessionId = (JSON.parse(raw) as { sessionId?: string }).sessionId;
          }
        } catch {
          /* ignore */
        }
        if (staleSessionId) {
          const accessToken = readStoredAccessToken();
          const envBase = process.env.NEXT_PUBLIC_API_BASE_URL as string;
          const contextPath = getApiContextPath();
          const { apiBase } = normalizeApiBaseUrl(envBase, { contextPath });
          await fetch(`${apiBase}/v1/sessions/${encodeURIComponent(staleSessionId)}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
          }).catch(() => undefined);
        }
        if (cancelled) return;
        clearSessionData();
        await wipeAppAuthState({
          preserveTheme: true,
          setLogoutFlags: false,
          sessionMessage: sessionMessage ?? null,
        });
      } catch {
        /* ignore */
      }
    };
    void wipeResidual();
    return () => {
      cancelled = true;
    };
  }, [authPhase, sessionMessage]);
  // If creating_session takes too long, show form so user can retry
  useEffect(() => {
    if (authPhase !== 'creating_session') {
      setSignInTimedOut(false);
      return;
    }
    const t = setTimeout(() => setSignInTimedOut(true), 10000);
    return () => clearTimeout(t);
  }, [authPhase]);

  // Add debug logging
  const logDebug = (message: string, data?: unknown) => {
    logger.debug(` [DOMAIN-AWARE-LOGIN] ${message}`, data || '');
  };

  const rememberAuthChoice = (email: string, method: LoginMethod) => {
    logDebug(`Remembering auth choice for ${email}: ${method}`);
    localStorage.setItem(`auth_choice_${email}`, method);
  };

  const getPreferredAuthMethod = (email: string): string | null => {
    const method = localStorage.getItem(`auth_choice_${email}`);
    logDebug(`Getting preferred auth method for ${email}: ${method}`);
    return method;
  };

  const isKnownUser = (email: string): boolean => {
    const known = localStorage.getItem(`auth_choice_${email}`) !== null;
    logDebug(`Checking if ${email} is known user: ${known}`);
    return known;
  };

  // CU-18: Generate OAuth state parameter for CSRF protection
  const generateState = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  // CU-18: Generate OAuth nonce parameter for replay protection
  const generateNonce = (): string => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  // Construct OIDC authorization URL from identity provider configuration
  // Uses generic OIDC config directly from backend (no mapping needed)
  const constructAuthorizationUrl = async (
    oidcConfig: OidcConfig | undefined,
    email: string
  ): Promise<string | null> => {
    if (!oidcConfig?.issuer || !oidcConfig?.clientId) {
      logDebug('Missing OIDC configuration for URL construction:', { oidcConfig });
      return null;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;
    const state = generateState();
    const nonce = generateNonce();
    
    // Generate PKCE code verifier and challenge (required by Keycloak)
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store state, nonce, and PKCE verifier for validation on callback
    // IMPORTANT: Store in BOTH sessionStorage and localStorage as fallback
    // Some browsers/security settings may clear sessionStorage on redirect
    try {
      // Primary storage: sessionStorage (cleared when tab closes)
      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_nonce', nonce);
      sessionStorage.setItem('oauth_code_verifier', codeVerifier);
      sessionStorage.setItem('keycloak_config', JSON.stringify({
        issuer: oidcConfig.issuer,
        clientId: oidcConfig.clientId,
        tokenEndpointPath: oidcConfig.tokenEndpointPath
      }));
      
      // Fallback storage: localStorage (more persistent, cleared after use)
      // Store with timestamp to auto-cleanup old entries
      const stateData = {
        state,
        nonce,
        codeVerifier,
        oidcConfig: {
          issuer: oidcConfig.issuer,
          clientId: oidcConfig.clientId,
          tokenEndpointPath: oidcConfig.tokenEndpointPath
        },
        timestamp: Date.now()
      };
      localStorage.setItem('oauth_state_fallback', JSON.stringify(stateData));
      
      // Verify storage was successful
      const storedState = sessionStorage.getItem('oauth_state');
      const storedFallback = localStorage.getItem('oauth_state_fallback');
      if (storedState !== state) {
        logDebug('WARNING: State storage verification failed in sessionStorage!', {
          expected: state,
          stored: storedState,
          hasFallback: !!storedFallback
        });
      } else {
        logDebug('State, nonce, and PKCE verifier stored successfully', {
          state,
          issuer: oidcConfig.issuer,
          clientId: oidcConfig.clientId,
          hasSessionStorage: !!storedState,
          hasFallback: !!storedFallback,
          hasCodeVerifier: !!codeVerifier
        });
      }
    } catch (e) {
      logDebug('ERROR: Failed to store state in storage', e);
      throw new Error('Failed to store authentication state. Please try again.');
    }

    // Construct authorization URL using generic OIDC function (with PKCE challenge)
    const authUrl = constructOidcAuthorizationUrl(oidcConfig, {
      redirectUri,
      email,
      state,
      nonce,
      codeChallenge
    });

    logDebug('Constructed OIDC authorization URL:', {
      url: authUrl,
      issuer: oidcConfig.issuer,
      clientId: oidcConfig.clientId,
      idpAlias: oidcConfig.idpAlias,
      redirectUri,
      hasState: !!state,
      hasNonce: !!nonce
    });

    return authUrl;
  };

  // Handle seamless redirect for known users
  const handleSeamlessRedirect = async (email: string) => {
    logDebug(`Handling seamless redirect for ${email}`);
    const preferredMethod = getPreferredAuthMethod(email);
    
    if (preferredMethod && preferredMethod !== 'auto') {
      logDebug(`Setting method to preferred: ${preferredMethod}`);
      setMethod(preferredMethod as LoginMethod);
      
      if (preferredMethod === 'sso' && domainConfig?.auth_url) {
        logDebug(`Redirecting to SSO: ${domainConfig.auth_url}`);
        // Redirect to SSO
        window.location.href = domainConfig.auth_url;
      } else if (preferredMethod === 'local') {
        logDebug('Continuing with local auth');
        // Continue with local auth
        setMethod('local');
      }
    }
  };

  // Domain discovery effect with debouncing
  useEffect(() => {
    // Clear any pending debounce when email or service changes
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    
    // Clear any pending retries when email or service changes
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    retryCountRef.current = 0;
    
    if (!email || !authenticationService || !authenticationServiceLoaded) {
      logDebug('Skipping domain discovery - missing requirements', {
        hasEmail: !!email,
        hasAuthService: !!authenticationService,
        authServiceLoaded: authenticationServiceLoaded
      });
      // Clear any previous errors when service isn't ready yet
      if (!authenticationServiceLoaded) {
        setError(null);
      }
      // Reset domain state when email is cleared
      if (!email) {
        setDomain('');
        setIsDomainValid(false);
        setDomainConfig(null);
        setIsDiscovering(false);
      }
      return;
    }

    const discoverDomain = async (isRetry = false) => {
      const emailDomain = email.split('@')[1];
      const emailDomainLowercase = emailDomain?.toLowerCase();
      logDebug(`Starting domain discovery for email: ${email}, domain: ${emailDomain}, lowercase: ${emailDomainLowercase}`, { isRetry, retryCount: retryCountRef.current });
      
      if (!emailDomain) {
        logDebug('No domain found in email');
        return;
      }

      // Validate domain format before making API call
      if (!validateDomain(emailDomain)) {
        logDebug(`Domain validation failed for: ${emailDomain}`);
        setDomain(emailDomain);
        setIsDiscovering(false);
        setDomainConfig(null);
        setIsDomainValid(false);
        setError(null); // Clear any previous errors for invalid domains
        return;
      }

      setDomain(emailDomain);
      setIsDiscovering(true);
      // Only clear error if this is not a retry (preserve error message during retries)
      if (!isRetry) {
        setError(null);
      }

      let isNetworkError = false;
      try {
        logDebug(`Making API call to discover domain: ${emailDomain} (will try both original and lowercase)`);
        logDebug('Authentication service state:', {
          serviceType: typeof authenticationService,
          hasDiscoverDomain: !!authenticationService.discoverDomain,
          apiBaseURL: process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8080',
          originalDomain: emailDomain,
          lowercaseDomain: emailDomainLowercase
        });

        // Use email-based discovery endpoint (CU-18: Update SSO flow)
        let result;
        try {
          result = await authenticationService.discoverByEmail({
            email: email
          });
        } catch (firstError) {
          logDebug(`Email discovery failed, error:`, firstError);
          throw firstError;
        }

        logDebug('Email discovery API response received:', {
          found: result.found,
          customerId: result.customerId,
          customerName: result.customerName,
          providerType: result.providerType,
          hasProviderConfig: !!result.providerConfig,
          hasOidcConfig: !!result.oidcConfig,
          idpAlias: result.oidcConfig?.idpAlias || result.keycloakAlias, // Support both for backward compatibility
          error: result.error,
          fullResult: result
        });
        
        // Log warning if SSO is configured but OIDC config is missing
        if (result.providerType && result.providerType !== 'local' && !result.oidcConfig) {
          logDebug('WARNING: SSO provider detected but oidcConfig is missing from backend response', {
            providerType: result.providerType,
            domain: emailDomain,
            result
          });
        }

        // Check if domain was actually found
        if (!result.found) {
          logDebug('Domain not found in API response');
          // Clear retry state on successful API response (even if domain not found)
          retryCountRef.current = 0;
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
          setDomainConfig(null);
          setIsDomainValid(false);
          setError(result.error || 'Domain not found. Please check your email or contact your administrator.');
          return;
        }
        
        // Success - clear retry state
        retryCountRef.current = 0;
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }

        // Domain was found, process the configuration
        logDebug('Domain found, processing result:', {
          found: result.found,
          customerId: result.customerId,
          customerName: result.customerName,
          providerType: result.providerType,
          hasProviderConfig: !!result.providerConfig
        });

        // Determine if SSO is available based on provider type (case-insensitive)
        const providerTypeLower = result.providerType?.toLowerCase() || '';
        const hasSSO = result.providerType && providerTypeLower !== 'local';
        
        // Try to get authorization URL from providerConfig
        // NOTE: The backend should provide the authorization URL or endpoint in providerConfig.
        // Provider-specific URL construction (Azure clouds, Google endpoints, etc.) should be
        // handled by the backend, not hardcoded in the frontend.
        // The preferred method is using oidcConfig to construct the OIDC authorization URL.
        const auth_url = result.providerConfig?.auth_url || 
                      result.providerConfig?.authorization_endpoint ||
                      result.providerConfig?.authorizationUrl ||
                      result.providerConfig?.authorization_url ||
                      result.providerConfig?.authority; // Some providers use 'authority' field

        // If no auth_url is provided by backend, log a warning
        // The backend should provide either:
        // 1. oidcConfig (preferred) - frontend constructs OIDC URL
        // 2. authorization URL in providerConfig (fallback) - backend provides complete URL
        if (!auth_url && hasSSO && !result.oidcConfig) {
          logDebug('WARNING: No authorization URL provided by backend for SSO provider. Backend should provide either oidcConfig or authorization URL in providerConfig.', {
            providerType: result.providerType,
            hasOidcConfig: !!result.oidcConfig,
            providerConfigKeys: result.providerConfig ? Object.keys(result.providerConfig) : []
          });
        }

        logDebug('SSO configuration:', {
          hasSSO,
          providerType: result.providerType,
          auth_url,
          providerConfig: result.providerConfig,
          fullResult: result
        });

        const mappedDomainConfig = {
          hasSSO,
          domain: emailDomain,
          customerId: result.customerId,
          customerName: result.customerName,
          providerType: result.providerType,
          authConfig: result.providerConfig,
          auth_url,
          ssoProvider: result.providerType,
          oidcConfig: result.oidcConfig,
          keycloakAlias: result.keycloakAlias || result.oidcConfig?.idpAlias
        };
        setDomainConfig(mappedDomainConfig);
        setIsDomainValid(true);

        // Automatically set method to SSO if SSO is available
        if (hasSSO) {
          setMethod('sso');
          
          // Log OIDC configuration for debugging
          logDebug('SSO detected - OIDC configuration:', {
            idpAlias: result.oidcConfig?.idpAlias || result.keycloakAlias,
            issuer: result.oidcConfig?.issuer,
            clientId: result.oidcConfig?.clientId,
            providerType: result.providerType,
            hasOidcConfig: !!result.oidcConfig,
            providerConfig: result.providerConfig
          });
          
          // Check if OIDC config is present - required for automatic redirect
          if (!result.oidcConfig) {
            logDebug('WARNING: SSO is enabled but oidcConfig is missing. This may indicate an identity provider configuration issue.');
            setError('SSO is configured but OIDC configuration is missing. Please contact your administrator.');
            return;
          }
          
          // Use auth context's login function which properly uses oidc-client-ts signinRedirect()
          // This ensures state is stored correctly in localStorage
          if (isKnownUser(email)) {
            // Known users get immediate redirect
            logDebug('Known user - using auth context login for SSO redirect');
            await login('sso', email, mappedDomainConfig);
          } else {
            // New users get a 1.5 second delay to see what's happening, then auto-redirect
            logDebug('Auto-redirecting to SSO in 1.5 seconds for new user');
            setTimeout(async () => {
              await login('sso', email, mappedDomainConfig);
            }, 1500);
          }
          return; // Exit early - redirect is happening
        } else {
          setMethod('local');
        }
      } catch (error) {
        logDebug('Domain discovery error:', error);
        
        // Handle different types of errors gracefully
        let errorMessage = 'Unable to verify domain configuration. Please try again.';
        isNetworkError = false;
        
        if (error instanceof Error) {
          const errorMsg = error.message.toLowerCase();
          const errorName = error.name?.toLowerCase() || '';
          const errorString = error.toString().toLowerCase();
          
          // Check for underlying cause (FetchError wraps NetworkError in cause property)
          const cause = (error as any).cause;
          const causeName = cause?.name?.toLowerCase() || '';
          const causeMsg = cause?.message?.toLowerCase() || '';
          const causeString = cause ? cause.toString().toLowerCase() : '';
          
          logDebug('Error message analysis:', { 
            errorMsg, 
            errorName, 
            errorString, 
            causeName,
            causeMsg,
            causeString,
            fullError: error 
          });
          
          // Check for network/connection errors first (common after frontend restart)
          // Include CORS errors, NetworkError, FetchError, and various network failure patterns
          // Also check the error string representation and underlying cause
          // FetchError wraps NetworkError in the cause property, so check both
          const isNetworkErrorType = errorName === 'networkerror' || errorName === 'fetcherror';
          const isNetworkErrorCause = causeName === 'networkerror' || 
            (causeName === 'typeerror' && (causeMsg.includes('networkerror') || causeMsg.includes('when attempting to fetch')));
          const hasNetworkKeywords = errorMsg.includes('network') || 
              errorMsg.includes('fetch') || 
              errorMsg.includes('timeout') ||
              errorMsg.includes('failed to fetch') || 
              errorMsg.includes('connection') ||
              errorMsg.includes('err_network') || 
              errorMsg.includes('networkerror') ||
              errorMsg.includes('cors') ||
              errorMsg.includes('cross-origin') ||
              errorMsg.includes('same origin policy') ||
              errorMsg.includes('request failed') ||
              errorMsg.includes('interceptors did not return') ||
              errorMsg.includes('did not succeed');
          const hasNetworkInString = errorString.includes('networkerror') ||
              errorString.includes('cors') ||
              errorString.includes('cross-origin') ||
              causeMsg.includes('networkerror') ||
              causeString.includes('networkerror');
          
          if (isNetworkErrorType || isNetworkErrorCause || hasNetworkKeywords || hasNetworkInString) {
            isNetworkError = true;
            errorMessage = 'Unable to connect to the server. The backend may still be starting up. Please wait a moment and try again.';
          } else if (errorMsg.includes('domain not found') || errorMsg.includes('404')) {
            // Only show "domain not found" if it's explicitly a 404, not a network error
            errorMessage = 'Domain not found. Please check your email or contact your administrator.';
          } else if (errorMsg.includes('unauthorized') || errorMsg.includes('403') || errorMsg.includes('forbidden')) {
            errorMessage = 'Access denied for this domain. Please contact your administrator.';
          } else if (errorMsg.includes('server') || errorMsg.includes('500')) {
            errorMessage = 'Server error. Please try again in a moment.';
          } else if (errorMsg.includes('invalid domain') || errorMsg.includes('malformed')) {
            errorMessage = 'Invalid domain format. Please check your email address.';
          }
        } else if (typeof error === 'object' && error !== null) {
          // Handle API error objects (including FetchError with cause)
          const errorObj = error as { status?: number; message?: string; code?: string; name?: string; cause?: any };
          const errorObjString = JSON.stringify(errorObj).toLowerCase();
          const causeObj = errorObj.cause;
          const causeName = causeObj?.name?.toLowerCase() || '';
          const causeMsg = causeObj?.message?.toLowerCase() || '';
          
          logDebug('API error object:', { errorObj, causeObj, causeName, causeMsg });
          
          // Check for network-related status codes, codes, or error names
          // Also check the cause property for underlying NetworkError
          if (errorObj.code === 'ECONNREFUSED' || errorObj.code === 'ETIMEDOUT' ||
              errorObj.code === 'ENOTFOUND' || 
              errorObj.name === 'NetworkError' || errorObj.name === 'FetchError' ||
              causeName === 'networkerror' ||
              causeName === 'typeerror' && (causeMsg.includes('networkerror') || causeMsg.includes('network')) ||
              errorObj.message?.toLowerCase().includes('network') ||
              errorObj.message?.toLowerCase().includes('cors') ||
              errorObj.message?.toLowerCase().includes('request failed') ||
              errorObj.message?.toLowerCase().includes('interceptors did not return') ||
              errorObjString.includes('networkerror') ||
              errorObjString.includes('cors') ||
              errorObjString.includes('cross-origin')) {
            isNetworkError = true;
            errorMessage = 'Unable to connect to the server. The backend may still be starting up. Please wait a moment and try again.';
          } else if (errorObj.status === 404) {
            errorMessage = 'Domain not found. Please check your email or contact your administrator.';
          } else if (errorObj.status === 403) {
            errorMessage = 'Access denied for this domain. Please contact your administrator.';
          } else if (errorObj.status && errorObj.status >= 500) {
            errorMessage = 'Server error. Please try again in a moment.';
          } else if (errorObj.message) {
            errorMessage = errorObj.message;
          }
        }
        
        // For 403 errors, we'll still allow users to proceed to enrollment
        // since it might be a new domain that needs to be set up
        if (errorMessage.includes('Access denied') || errorMessage.includes('403')) {
          setError('This domain is not registered. Please contact your administrator or set up your organization.');
          setDomainConfig(null);
          setIsDomainValid(false);
        } else if (!isNetworkError) {
          // Only invalidate domain for non-network errors
          setError(errorMessage);
          setDomainConfig(null);
          setIsDomainValid(false);
        } else {
          // For network errors, show message but don't invalidate domain (allows retry)
          setError(errorMessage);
          
          // Automatic retry for network errors (max 3 retries with exponential backoff)
          if (isNetworkError && retryCountRef.current < 3) {
            retryCountRef.current += 1;
            const retryDelay = Math.min(1000 * Math.pow(2, retryCountRef.current - 1), 5000); // 1s, 2s, 4s, max 5s
            logDebug(`Scheduling retry ${retryCountRef.current}/3 in ${retryDelay}ms`);
            
            retryTimeoutRef.current = setTimeout(() => {
              logDebug(`Retrying domain discovery (attempt ${retryCountRef.current})`);
              discoverDomain(true);
            }, retryDelay);
          } else if (isNetworkError && retryCountRef.current >= 3) {
            logDebug('Max retries reached, stopping automatic retry');
            // After max retries, update error message to indicate backend is not available
            setError('Unable to connect to the server. Please ensure the backend is running and try again.');
          }
        }
      } finally {
        if (!isRetry || !isNetworkError || retryCountRef.current >= 3) {
          setIsDiscovering(false);
        }
        logDebug('Domain discovery completed', { isRetry, isNetworkError, retryCount: retryCountRef.current });
      }
    };

    // Debounce domain discovery to avoid triggering on every keystroke
    // Wait 500ms after user stops typing before making API call
    debounceTimeoutRef.current = setTimeout(() => {
      logDebug('Debounce completed, starting domain discovery');
      discoverDomain();
    }, 500);
    
    return () => {
      // Cleanup: clear debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      // Cleanup: clear retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [email, authenticationService, authenticationServiceLoaded]);

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    logDebug('Local login initiated', { email, domainConfig });
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!domainConfig) {
      setError('Please wait for domain verification to complete');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Remember user's auth choice
      rememberAuthChoice(email, 'local');
      logDebug('Calling login function with local method', { email, domainConfig });
      // Use the auth context to handle local authentication with domain config
      await login('local', email, domainConfig);
    } catch (error) {
      logDebug('Local login error:', error);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (errorMsg.includes('unauthorized') || errorMsg.includes('forbidden')) {
          errorMessage = 'Access denied. Please check your credentials.';
        } else if (errorMsg.includes('server') || errorMsg.includes('500')) {
          errorMessage = 'Server error. Please try again in a moment.';
        }
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    logDebug('SSO login initiated', { email, domainConfig });
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!domainConfig) {
      setError('Please wait for domain verification to complete');
      return;
    }

    // Use auth context's login function which properly uses oidc-client-ts signinRedirect()
    // This ensures state is stored correctly in localStorage in the format oidc-client-ts expects
    // DO NOT manually construct URLs - let oidc-client-ts handle it
    if (domainConfig.oidcConfig) {
      setIsLoading(true);
      setError(null);
      
      try {
        // Remember user's auth choice
        rememberAuthChoice(email, 'sso');
        
        logDebug('Using auth context login for SSO redirect (oidc-client-ts)', {
          oidcConfig: domainConfig.oidcConfig,
          providerType: domainConfig.providerType
        });
        
        // This will call redirectToSpecificSSO which uses oidc-client-ts signinRedirect()
        // The library will handle state storage in localStorage automatically
        await login('sso', email, domainConfig);
        return; // Exit early - redirect is happening
      } catch (error) {
        logDebug('SSO redirect error:', error);
        setError('Failed to redirect to SSO provider. Please try again.');
        setIsLoading(false);
        return;
      }
    }

    // Fallback: Check for OIDC config - this is critical for redirecting to the correct identity provider
    if (!domainConfig.oidcConfig) {
      logDebug('SSO oidcConfig missing - identity provider configuration issue:', {
        domainConfig,
        providerType: domainConfig.providerType,
        hasSSO: domainConfig.hasSSO,
        customerName: domainConfig.customerName
      });
      setError('SSO is configured but OIDC configuration is missing. The identity provider may not be properly configured. Please contact your administrator.');
      return;
    }

    if (!domainConfig.auth_url) {
      logDebug('SSO auth_url missing:', {
        domainConfig,
        providerConfig: domainConfig.authConfig,
        hasSSO: domainConfig.hasSSO,
        providerType: domainConfig.providerType
      });
      setError('SSO configuration not available. Please contact your administrator.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Remember user's auth choice
      rememberAuthChoice(email, 'sso');
      logDebug('Calling login function with SSO method', {
        email,
        domainConfig,
        providerType: domainConfig.providerType,
        idpAlias: (domainConfig as any).oidcConfig?.idpAlias || domainConfig.keycloakAlias,
        authConfig: domainConfig.authConfig
      });
      // Use the auth context to handle SSO authentication with domain config
      await login('sso', email, domainConfig);
    } catch (error) {
      logDebug('SSO login error:', error);
      let errorMessage = 'Failed to redirect to SSO provider. Please try again.';
      
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (errorMsg.includes('invalid url') || errorMsg.includes('malformed')) {
          errorMessage = 'SSO configuration error. Please contact your administrator.';
        }
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateDomain = (domain: string) => {
    // Basic domain validation - must have at least one character before and after the dot
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return domainRegex.test(domain) && domain.length > 0 && !domain.endsWith('.');
  };

  // Add function to redirect to Keycloak for account creation
  const redirectToKeycloakForAccountCreation = async (email?: string) => {
    try {
      const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY;
      const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID;
      
      if (!authority || !clientId) {
        throw new Error('OIDC configuration missing');
      }

      console.log('DomainAwareLogin: Redirecting to Keycloak for account creation');

      // Create UserManager for this specific auth flow
      const userManager = new UserManager({
        authority,
        client_id: clientId,
        redirect_uri: `${window.location.origin}/auth/callback`,
        post_logout_redirect_uri: `${window.location.origin}/`,
        response_type: 'code',
        scope: 'openid profile email',
        automaticSilentRenew: false,
        silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
        userStore: new WebStorageStateStore({ store: window.localStorage }),
        filterProtocolClaims: true,
        loadUserInfo: true,
        monitorSession: false,
        accessTokenExpiringNotificationTimeInSeconds: 60,
        includeIdTokenInSilentRenew: false,
      });
      
      // Use the library's signinRedirect method with registration hint
      await userManager.signinRedirect({
        extraQueryParams: {
          login_hint: email || '',
          kc_action: 'register'
        }
      });
    } catch (error) {
      console.error('DomainAwareLogin: Error redirecting to Keycloak:', error);
      setError('Failed to redirect to account creation. Please try again.');
    }
  };

  // Use authPhase for reliable UI (see AUTH_DESIGN_AND_HARDENING.md)
  if (authPhase === 'authenticated') {
    return (
      <div className={`min-h-screen ${getBackgroundClasses(theme, 'page')} flex items-center justify-center`}>
        <p className={getTextClasses(theme, 'muted')}>Redirecting to dashboard...</p>
      </div>
    );
  }
  if (authPhase === 'creating_session' && !signInTimedOut) {
    return (
      <div className={`min-h-screen ${getBackgroundClasses(theme, 'page')} flex items-center justify-center`}>
        <p className={getTextClasses(theme, 'muted')}>Completing sign-in...</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${getBackgroundClasses(theme, 'page')} flex items-center justify-center p-4`}>
      <Card className={`w-full max-w-md ${getBackgroundClasses(theme, 'card')} backdrop-blur-sm border`} style={{ backgroundColor: 'var(--theme-card)', borderColor: 'var(--theme-border-dialog)' }}>
        <CardHeader className="text-center pb-6">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${getAccentClasses(theme, 'primary')}`} style={{ background: 'linear-gradient(to bottom right, var(--theme-accent), var(--theme-info))' }}>
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className={`text-2xl font-bold ${getTextClasses(theme)}`}>
            Sign In
          </CardTitle>
          <p className={`${getTextClasses(theme, 'muted')} text-sm`}>
            Access your organization's security platform
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Timed out waiting for session: let user retry */}
          {signInTimedOut && (
            <div className={`rounded-lg p-3 ${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} border border-[color:var(--theme-warning)]`}>
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 shrink-0" style={{ color: 'var(--theme-warning)' }} />
                <p className="text-sm" style={{ color: 'var(--theme-warning)' }}>
                  Sign-in is taking longer than expected. Please try again below.
                </p>
              </div>
            </div>
          )}
          {/* Session Message */}
          {sessionMessage && (
            <div
              className={`rounded-lg p-3 border ${getBorderClasses(theme)}`}
              style={{
                backgroundColor: 'color-mix(in srgb, var(--theme-warning) 14%, transparent)',
                borderColor: 'color-mix(in srgb, var(--theme-warning) 45%, transparent)',
              }}
            >
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 shrink-0" style={{ color: 'var(--theme-warning)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--theme-warning)' }}>
                  {sessionMessage}
                </p>
              </div>
            </div>
          )}

          {/* Email Input */}
          <div className="space-y-2">
            <label htmlFor="email" className={`text-sm font-medium ${getTextClasses(theme)}`}>
              Email Address
            </label>
            <div className="relative">
              <Building2 className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${getTextClasses(theme, 'muted')}`} />
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  setError(null);
                  // Clear domain state immediately when email changes to avoid showing stale data
                  if (!newEmail || !newEmail.includes('@')) {
                    setDomain('');
                    setIsDomainValid(false);
                    setDomainConfig(null);
                    setIsDiscovering(false);
                  }
                }}
                onFocus={(e) => {
                  // Prevent extension errors from breaking the app
                  try {
                    // Ensure the input element has proper structure for browser extensions
                    if (e.target && !e.target.hasAttribute('data-email-input')) {
                      e.target.setAttribute('data-email-input', 'true');
                    }
                  } catch (err) {
                    // Silently ignore extension-related errors
                  }
                }}
                placeholder="you@company.com"
                className={`pl-10 ${getInputClasses(theme)}`}
                disabled={isLoading}
                required
                data-testid="email-input"
              />
            </div>
          </div>

          {/* Domain Discovery Status */}
          {domain && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Globe className={`w-4 h-4 ${getTextClasses(theme)}`} />
                <span className={`text-sm ${getTextClasses(theme)}`}>Domain: {domain}</span>
                {isDiscovering && (
                  <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--theme-accent)', borderTopColor: 'transparent' }} />
                )}
                {isDomainValid && !isDiscovering && (
                  <CheckCircle className="w-4 h-4" style={{ color: 'var(--theme-success)' }} />
                )}
                {!isDiscovering && !isDomainValid && validateDomain(domain) && (
                  <AlertCircle className="w-4 h-4" style={{ color: 'var(--theme-warning)' }} />
                )}
                {!isDiscovering && !isDomainValid && !validateDomain(domain) && (
                  <AlertCircle className={`w-4 h-4 ${getTextClasses(theme, 'muted')}`} />
                )}
              </div>
              
              {domainConfig && (
                <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--theme-info-box-bg)' }}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${getTextClasses(theme)}`}>
                      {domainConfig.customerName || 'Organization'}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full" style={{ 
                      backgroundColor: 'var(--theme-info-pill-bg)',
                      color: 'var(--theme-info)'
                    }}>
                      {domainConfig.hasSSO ? 'SSO Enabled' : 'Local Auth'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}



          {/* Error Display */}
          {error && (
            <div
              className={`rounded-lg p-3 border ${getBorderClasses(theme)}`}
              style={{
                backgroundColor: 'color-mix(in srgb, var(--theme-error) 14%, transparent)',
                borderColor: 'color-mix(in srgb, var(--theme-error) 45%, transparent)',
              }}
            >
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 mr-2 shrink-0" style={{ color: 'var(--theme-error)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--theme-error)' }}>
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Sign Up Option for Unknown Domains */}
          {error && (error.includes('not registered') || error.includes('Access denied') || error.includes('403')) && (
            <div
              className={`rounded-lg p-4 border ${getBorderClasses(theme)}`}
              style={{
                backgroundColor: 'color-mix(in srgb, var(--theme-info) 14%, transparent)',
                borderColor: 'color-mix(in srgb, var(--theme-info) 45%, transparent)',
              }}
            >
              <div className="text-center">
                <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--theme-info)' }}>
                  New Organization?
                </h3>
                <p className={`text-xs mb-4 ${getTextClasses(theme)}`}>
                  Set up your organization to get started with our security platform.
                </p>
                <Button
                  onClick={() => {
                    const enrollUrl = domain ? `/enroll?domain=${encodeURIComponent(domain)}` : '/enroll';
                    window.location.href = enrollUrl;
                  }}
                  className={`w-full text-sm ${getAccentClasses(theme, 'primary')}`}
                  style={{ backgroundColor: 'var(--theme-info)', color: 'var(--theme-foreground)' }}
                >
                  Set Up Organization
                </Button>
              </div>
            </div>
          )}



          {/* Submit Button */}
          {isDomainValid && domainConfig && (
            method === 'local' ? (
              <form onSubmit={handleLocalLogin}>
                <Button
                  type="submit"
                  disabled={!email || !validateEmail(email) || isLoading}
                  className={`w-full text-white ${getAccentClasses(theme, 'primary')}`}
                  style={{ background: 'linear-gradient(to right, var(--theme-accent), var(--theme-info))' }}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Redirecting...
                    </>
                  ) : (
                    <>
                      Continue to Sign In
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <Button
                onClick={handleSSOLogin}
                disabled={!email || !validateEmail(email) || isLoading}
                className={`w-full text-white ${getAccentClasses(theme, 'primary')}`}
                  style={{ background: 'linear-gradient(to right, var(--theme-info), var(--theme-accent))' }}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    Continue with SSO
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            )
          )}

          {/* Help Text */}
          <div className="text-center">
            <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
              {!domain 
                ? 'Enter your email to discover your organization\'s login options'
                : !validateDomain(domain)
                ? 'Please complete your email address to check domain configuration'
                : !isDomainValid && !isDiscovering
                ? (error && error.includes('Unable to connect'))
                  ? 'Connection issue detected. Please wait a moment and try again.'
                  : 'Domain not found. Please check your email or contact your administrator.'
                : !isDomainValid && isDiscovering
                ? 'We are checking your domain configuration...'
                : method === 'local' 
                ? 'We will redirect you to sign in securely'
                : 'We will redirect you to your organization\'s SSO provider'
              }
            </p>
          </div>

          {/* Additional Links */}
          <div className="text-center space-y-2">
            {enrollmentEnabled && (
              <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    redirectToKeycloakForAccountCreation(email);
                  }}
                  className={`underline ${getTextClasses(theme)}`}
                  style={{ color: 'var(--theme-accent)' }}
                >
                  Set up your organization
                </button>
              </p>
            )}
            {isDomainValid && domainConfig && (
              <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
                <button
                  type="button"
                  onClick={() => {
                    rememberAuthChoice(email, 'auto');
                    setError('Auto-sign in enabled for this email. Refresh the page to use it.');
                  }}
                  className={`underline ${getTextClasses(theme)}`}
                  style={{ color: 'var(--theme-success)' }}
                >
                  Remember my choice for auto-sign in
                </button>
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 